import { useAnalyticsEventTracker } from '@/gaEvents';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import AlertIcon from '@/icons/amaruNewIcons/AlertIcon';
import ReportsIcon from '@/icons/amaruNewIcons/ReportsIcon';
import { useSelector } from '@/store';
import { DevicesAnalysisDevice } from '@features/devicesAnalysis/hooks/useDevicesAnalysis';
import { dataToTable } from '@features/devicesAnalysis/utils/dataToTable';
import ReportProblemDialog from '@features/reportProblem';
import { CreateReportDataExtractionDialog } from '@features/reports/components/creationSection/CreateReportDataExtractionDialog';
import DateTimeRangePicker, {
  formatDateTimeRangeLabel,
  isRelativeTimeRange,
  RangeTimeValue,
} from '@features/standardDesign/components/DateTimeRangePicker';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Download } from '@mui/icons-material';
import { Autocomplete, Box, Button, IconButton, TextField } from '@mui/material';
import { TransitionStartFunction, useState, type FC } from 'react';
import { useDispatch } from 'react-redux';
import DeviceMenu from 'src/features/deviceMenu/components/DeviceMenu';
import { useFormatters } from 'src/hooks/useFormatters';
import GearIcon from 'src/icons/amaruNewIcons/GearIcon';
import { setParameters, TelemetryKey } from '../../slices/devicesAnalysisSlice';
import { MetricField } from '@/__generated__/graphql';
import { getMetricUnits } from '@features/reports/utils/metricHelpers';
import { Access, useSuspenseAuthorization } from '@features/authorization';

const windowOptions = [
  {
    label: msg`Dato bruto`,
    window: null,
  },
  {
    label: msg`1 minuto`,
    window: '1m',
  },
  {
    label: msg`5 minutos`,
    window: '5m',
  },
  {
    label: msg`10 minutos`,
    window: '10m',
  },
  {
    label: msg`15 minutos`,
    window: '15m',
  },
  {
    label: msg`30 minutos`,
    window: '30m',
  },
  {
    label: msg`1 hora`,
    window: '1h',
  },
  {
    label: msg`6 horas`,
    window: '6h',
  },
  {
    label: msg`12 horas`,
    window: '12h',
  },
  {
    label: msg`1 día`,
    window: '1d',
  },
  {
    label: msg`3 días`,
    window: '3d',
  },
];

interface Props {
  device: DevicesAnalysisDevice;
  startTransition: TransitionStartFunction;
}

const Toolbar: FC<Props> = ({ device, startTransition }) => {
  const [openReportModal, setOpenReportModal] = useState(false);
  const { setModalState: setDownloadDataModalState } = useGlobalModal('downloadData');
  const { _, i18n } = useLingui();
  const { formatDateTimeRange } = useFormatters();
  const dispatch = useDispatch();
  const { clickEvent, selectorEvent } = useAnalyticsEventTracker('Station Analysis');
  const [showReportDialog, setShowReportDialog] = useState(false);
  const { validateAccess } = useSuspenseAuthorization();
  const reportModalAccess = validateAccess(Access.CreateDevice);
  const { parameters, telemetry, data } = useSelector((state) => state.devices_analysis_store);

  const handleReport = () => {
    clickEvent({ label: 'report-problem' });
    setShowReportDialog(true);
  };

  // const handleExpand = () => {
  //   clickEvent({ label: 'expand-chart' });
  // };
  //
  // const handleDownload = () => {
  //   clickEvent({ label: 'download-chart' });
  // };
  //
  const handleChangeWindow = (value: string | null) => {
    selectorEvent({ label: 'window', value: value ?? 'Dato bruto' });
    startTransition(() => {
      dispatch(
        setParameters({
          window: value,
        }),
      );
    });
  };

  const handleChangeTimeRange = (value: RangeTimeValue) => {
    selectorEvent({
      label: 'period',
      value: formatDateTimeRangeLabel(value, i18n, formatDateTimeRange),
    });
    startTransition(() => {
      dispatch(
        setParameters({
          start: value.start,
          stop: value.stop,
        }),
      );
    });
  };

  const isRelativeRange = isRelativeTimeRange({ start: parameters.start, stop: parameters.stop });

  return (
    <>
      <ReportProblemDialog
        device={{
          id: device.id,
          name: device.profile.name,
          type: device.profile.type,
          organization_name: device?.profile?.organization?.name,
        }}
        show={showReportDialog}
        handleClose={() => setShowReportDialog(false)}
      />
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr',
          sm: 'repeat(2, 1fr) auto',
        }}
        gap={2}
      >
        <DateTimeRangePicker
          value={{
            start: parameters.start,
            stop: parameters.stop,
          }}
          onChange={(value) => {
            handleChangeTimeRange(value);
          }}
          onChangeWindow={(value) => {
            handleChangeWindow(value);
          }}
        />
        <Autocomplete
          size="small"
          options={windowOptions}
          value={windowOptions.find((option) => option.window === parameters.window)}
          getOptionLabel={(option) => _(option.label)}
          onChange={(_, value) => {
            handleChangeWindow(value.window);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={_(
                msg({
                  message: 'Promediar por periodo',
                  comment:
                    'Análisis de estaciones - etiqueta de campo de selección de ventana de promedio',
                }),
              )}
            />
          )}
          disableClearable
        />
        <Box display="flex" gap={1}>
          <Button
            onClick={handleReport}
            color="warning"
            variant="outlined"
            sx={{ justifySelf: 'flex-end' }}
          >
            <Trans comment="Análisis de estaciones - botón de reportar problema">
              Reportar un problema
            </Trans>{' '}
            <AlertIcon sx={{ ml: 2 }} />
          </Button>
          <DeviceMenu CustomIcon={() => <GearIcon color="info" />} deviceId={device.id} />
          {reportModalAccess && (
            <IconButton onClick={() => setOpenReportModal(true)}>
              <ReportsIcon />
            </IconButton>
          )}
          {parameters.deviceId && reportModalAccess && (
            <CreateReportDataExtractionDialog
              open={openReportModal}
              onClose={() => setOpenReportModal(false)}
              initialValues={{
                devices: Array.from(
                  new Set([parameters.deviceId, ...parameters.comparisonDeviceIds]),
                ),
                createExcel: true,
                createPdf: true,
                meanWindow: parameters.window ?? undefined,
                relativeDate: isRelativeRange ? parameters.start : undefined,
                startDate: !isRelativeRange ? new Date(parameters.start).getTime() : undefined,
                stopDate:
                  !isRelativeRange && parameters.stop
                    ? new Date(parameters.stop).getTime()
                    : undefined,
                metrics: (() => {
                  const metrics: { metricName: MetricField; unit: string }[] = [];
                  const pushMetric = (metricName: MetricField) => {
                    if (telemetry.availableFields.includes(metricName)) {
                      metrics.push({
                        metricName,
                        unit: getMetricUnits(metricName)[0]?.value,
                      });
                    }
                  };

                  if (telemetry.accumulatedVol) pushMetric(MetricField.AccumulatedVol);
                  if (telemetry.angle) pushMetric(MetricField.Angle);
                  if (telemetry[TelemetryKey.DissolvedOxygen])
                    pushMetric(MetricField.DissolvedOxygen);
                  if (telemetry[TelemetryKey.DissolvedOxygenSaturation])
                    pushMetric(MetricField.DissolvedOxygenSaturation);
                  if (telemetry.flow) pushMetric(MetricField.Flow);
                  if (telemetry.level) pushMetric(MetricField.Level);
                  if (telemetry[TelemetryKey.OriginalDistance])
                    pushMetric(MetricField.OriginalDistance);
                  if (telemetry[TelemetryKey.Orp]) pushMetric(MetricField.Orp);
                  if (telemetry[TelemetryKey.Ph]) pushMetric(MetricField.Ph);
                  if (telemetry[TelemetryKey.Position]) pushMetric(MetricField.Position);
                  if (telemetry[TelemetryKey.SnowLevel]) pushMetric(MetricField.SnowLevel);
                  if (telemetry[TelemetryKey.SnowLevelNonCalibrated])
                    pushMetric(MetricField.SnowLevelNonCalibrated);
                  if (telemetry[TelemetryKey.SnowRadiation]) pushMetric(MetricField.SnowRadiation);
                  if (telemetry[TelemetryKey.SnowTemperature])
                    pushMetric(MetricField.SnowTemperature);
                  if (telemetry[TelemetryKey.SnowWaterEquivalent])
                    pushMetric(MetricField.SnowWaterEquivalent);
                  if (telemetry[TelemetryKey.SnowWeight]) pushMetric(MetricField.SnowWeight);
                  if (telemetry[TelemetryKey.Temperature]) pushMetric(MetricField.Temperature);
                  if (telemetry[TelemetryKey.TurbidityFnu]) pushMetric(MetricField.TurbidityFnu);
                  if (telemetry[TelemetryKey.TurbidityNtu]) pushMetric(MetricField.TurbidityNtu);
                  if (telemetry[TelemetryKey.Velocity]) pushMetric(MetricField.Velocity);
                  if (telemetry[TelemetryKey.VolumeDay]) pushMetric(MetricField.VolumeDay);
                  if (telemetry[TelemetryKey.VolumeHour]) pushMetric(MetricField.VolumeHour);
                  if (telemetry[TelemetryKey.VolumeMonth]) pushMetric(MetricField.VolumeMonth);

                  return metrics;
                })(),
              }}
            />
          )}
          <IconButton
            onClick={() => {
              if (data) {
                const { headers, rows } = dataToTable(data, i18n);
                setDownloadDataModalState({
                  open: true,
                  headers,
                  rows,
                });
              }
            }}
          >
            <Download />
          </IconButton>
        </Box>
      </Box>
    </>
  );
};

export default Toolbar;
